import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useToast } from "../../hooks/use-toast";
import { vendorService } from "../../services/vendorService";
import { subcontractorService } from "../../services/subcontractorService";

/**
 * Quick-add dialog to create a vendor OR subcontractor without leaving the
 * material/receipt form. On success, calls onCreated({ id, name, type }) so the
 * parent can refresh its lists and auto-select the new entity.
 */
export default function QuickAddVendorDialog({ open, onOpenChange, onCreated, defaultName = "" }) {
  const { toast } = useToast();
  const [entityType, setEntityType] = useState("vendor"); // 'vendor' or 'subcontractor'
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: defaultName,
    phone: "",
    email: "",
    address: "",
    specialty: "",  // subcontractor only
    contact: "",    // vendor only
  });

  // Reset the form whenever the dialog opens
  React.useEffect(() => {
    if (open) {
      setForm({ name: defaultName, phone: "", email: "", address: "", specialty: "", contact: "" });
      setEntityType("vendor");
    }
  }, [open, defaultName]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ variant: "destructive", description: "Name is required" });
      return;
    }
    setSaving(true);
    try {
      if (entityType === "vendor") {
        const resp = await vendorService.create({
          company_name: form.name.trim(),
          phone: form.phone || undefined,
          email: form.email || undefined,
          address: form.address || undefined,
          contact: form.contact || undefined,
        });
        const created = resp?.data || resp;
        onCreated({
          id: created.id || created._id,
          name: created.company_name || form.name.trim(),
          type: "vendor",
        });
      } else {
        const resp = await subcontractorService.create({
          name: form.name.trim(),
          phone: form.phone || undefined,
          email: form.email || undefined,
          address: form.address || undefined,
          specialty: form.specialty || undefined,
        });
        const created = resp?.data || resp;
        onCreated({
          id: created.id || created._id,
          name: created.name || form.name.trim(),
          type: "subcontractor",
        });
      }
      toast({ description: `${entityType === "vendor" ? "Vendor" : "Subcontractor"} added` });
      onOpenChange(false);
    } catch (e) {
      toast({ variant: "destructive", description: e?.detail || e?.message || "Failed to add" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Add Vendor / Subcontractor</DialogTitle>
          <DialogDescription>Create a new one and it will be selected automatically.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Type toggle */}
          <div className="flex gap-1 border rounded p-1">
            <button
              type="button"
              onClick={() => setEntityType("vendor")}
              className={`flex-1 px-3 py-1.5 text-sm rounded ${entityType === "vendor" ? "bg-brand-600 text-white" : "hover:bg-gray-100"}`}
            >Vendor</button>
            <button
              type="button"
              onClick={() => setEntityType("subcontractor")}
              className={`flex-1 px-3 py-1.5 text-sm rounded ${entityType === "subcontractor" ? "bg-brand-600 text-white" : "hover:bg-gray-100"}`}
            >Subcontractor</button>
          </div>

          <div>
            <Label className="text-xs">{entityType === "vendor" ? "Company Name *" : "Name *"}</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={entityType === "vendor" ? "e.g. Home Depot" : "e.g. Beau's Crew"} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
          </div>

          <div>
            <Label className="text-xs">Address</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>

          {entityType === "vendor" ? (
            <div>
              <Label className="text-xs">Contact Person</Label>
              <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
            </div>
          ) : (
            <div>
              <Label className="text-xs">Specialty</Label>
              <Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} placeholder="e.g. Fencing, Concrete" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-brand-600 hover:bg-brand-700">
            {saving ? "Adding..." : "Add & Select"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
